/**
 * Her state on the device (spec §6): load/adopt, save, and the dailies record.
 * Carry-over across a vocabulary change keeps the counts and games (the
 * alphabets do not change) and starts a fresh atlas: the old sorted word list
 * is not on the device, so the by-word carry-over of spec §6 is not possible
 * here (Ruling recorded in the phase 3 ledger).
 */
import {
  createState,
  deserialize,
  FEATURE_SIZES,
  serialize,
  type AriadneIds,
  type AriadneState,
  type MatchVerdict,
} from "@synapse/engine";

import { unifiedDataStore } from "../services/UnifiedDataStore";
import type { AriadneRecord } from "../services/unifiedDataSchema";

const countsOf = (raw: unknown): number[][] | null => {
  if (!Array.isArray(raw) || raw.length !== FEATURE_SIZES.length) return null;
  const rows = raw.map((row, i) =>
    Array.isArray(row) &&
    row.length === FEATURE_SIZES[i] &&
    row.every((v) => Number.isInteger(v) && v >= 0)
      ? ([...row] as number[])
      : null,
  );
  return rows.every((r) => r !== null) ? (rows as number[][]) : null;
};

/** The stored record for these assets: birth, the same state, or a carried-over one. */
export function adoptState(
  raw: unknown,
  ids: AriadneIds,
  n: number,
): AriadneState {
  if (raw === null || raw === undefined) return createState(ids, n);
  try {
    const s = deserialize(raw, n, { vocab_hash: ids.vocab_hash });
    return { ...s, graph_hash: ids.graph_hash, ariadne_hash: ids.ariadne_hash };
  } catch (e) {
    const r = raw as {
      vocab_hash?: unknown;
      games?: unknown;
      n_taken?: unknown;
      n_passed?: unknown;
    };
    const taken = countsOf(r.n_taken);
    const passed = countsOf(r.n_passed);
    const vocabChanged =
      typeof r.vocab_hash === "string" && r.vocab_hash !== ids.vocab_hash;
    if (
      vocabChanged &&
      taken &&
      passed &&
      Number.isInteger(r.games) &&
      (r.games as number) >= 0
    ) {
      const next = createState(ids, n);
      next.games = r.games as number;
      next.n_taken = taken;
      next.n_passed = passed;
      return next;
    }
    console.error("ariadne: stored state unreadable, starting from birth:", e);
    return createState(ids, n);
  }
}

export async function loadState(
  ids: AriadneIds,
  n: number,
): Promise<AriadneState> {
  const section = await unifiedDataStore.getAriadne();
  return adoptState(section.state, ids, n);
}

export async function saveState(s: AriadneState): Promise<void> {
  await unifiedDataStore.setAriadneState(serialize(s));
}

/** Not behind her: ahead, tied, or she was still out there (spec §5 streak). */
export const notBehind = (v: MatchVerdict): boolean =>
  v === "ahead" || v === "tied" || v === "left_behind";

/** Pure: the record after one game she learned from (spec §5, D10). A give-up breaks the streak and is counted as one. */
export function nextRecord(
  r: AriadneRecord,
  verdict: MatchVerdict,
): AriadneRecord {
  const streak = notBehind(verdict) ? r.streak + 1 : 0;
  return {
    ahead: r.ahead + (verdict === "ahead" ? 1 : 0),
    behind: r.behind + (verdict === "behind" ? 1 : 0),
    tied: r.tied + (verdict === "tied" ? 1 : 0),
    left_behind: r.left_behind + (verdict === "left_behind" ? 1 : 0),
    // Records saved before give-ups were counted have no field.
    gave_up: (r.gave_up ?? 0) + (verdict === "gave_up" ? 1 : 0),
    streak,
    best: Math.max(r.best, streak),
  };
}

export function popcount(bits: Uint8Array): number {
  let n = 0;
  for (const b of bits) {
    let v = b;
    while (v) {
      v &= v - 1;
      n++;
    }
  }
  return n;
}
